import { NameForms, PhoneForm, AddressForms } from "@/components/fragments/forms";
import { Flex } from "@chakra-ui/react";
import { Box } from "@/components/essentials/box";
import { Typography } from "@/components/essentials/typography";
import { useState, type FC } from "react";
import { EmailForm } from "@/components/fragments/forms/email-form";
import {
  contractorFormAddressField,
  contractorFormBuildingNameAndRoomNumberField,
  contractorFormCityField,
  contractorFormDescriptionTestId,
  contractorFormFirstNameField,
  contractorFormLastNameField,
  contractorFormMailField,
  contractorFormPhoneNumberField,
  contractorFormPrefectureField,
  contractorFormTestId,
  contractorFormTitleTestId,
  contractorFormZipCodeField,
} from "./const";
import { FormLabel } from "@/components/essentials/form-control";
import { useFormContext } from "react-hook-form";
import { Button } from "@/components/essentials/button";
import { FadeableBox } from "@/components/fragments/box/fadeable-box/FadeableBox";
import { ChatInputBox } from "@/components/fragments/box/chat-input-box";

export interface ContractorFormProps {
  readonly canInputEmail?: boolean;
  readonly email?: string;
  readonly isStepwise?: boolean;
  readonly showChatBalloon?: boolean;
  readonly onCompleteAllSteps?: () => void;
}

export const ContractorForm: FC<ContractorFormProps> = ({
  canInputEmail = true,
  email = "",
  isStepwise = false,
  showChatBalloon = false,
  onCompleteAllSteps,
}) => {
  const {
    register,
    formState: { isValid },
  } = useFormContext();
  const [formStepIndex, setFormStepIndex] = useState<number>(0);

  return (
    <Flex flexDir="column" gridGap="10" data-testid={contractorFormTestId}>
      {!showChatBalloon && (
        <Box wordBreak="break-word" textAlign="center">
          <Typography variant="display3" data-testid={contractorFormTitleTestId}>
            ご契約者情報の入力
          </Typography>
          <Typography variant="text4" color="gray.dark" data-testid={contractorFormDescriptionTestId}>
            ご契約者情報を入力してください。
          </Typography>
        </Box>
      )}
      <Flex flexDir="column" gridGap="1.5rem">
        <ChatInputBox showChatBalloon={showChatBalloon} title="メールアドレスを教えてください">
          {canInputEmail ? (
            <EmailForm emailField={contractorFormMailField} />
          ) : (
            <Box>
              <FormLabel>メールアドレス</FormLabel>
              <Typography
                variant="text2"
                color="mono.dark.87"
                px={4}
                data-testid={contractorFormMailField + "-email"}
                data-dd-privacy="mask"
              >
                {email}
              </Typography>
              <input type="hidden" value={email} {...register(contractorFormMailField)} />
            </Box>
          )}
        </ChatInputBox>

        <FadeableBox fadeInCondition={formStepIndex >= 1} isAlwaysDisplay={!isStepwise}>
          <ChatInputBox showChatBalloon={showChatBalloon} title="お名前を教えてください">
            <NameForms firstNameField={contractorFormFirstNameField} lastNameField={contractorFormLastNameField} />
          </ChatInputBox>
        </FadeableBox>

        <FadeableBox fadeInCondition={formStepIndex >= 2} isAlwaysDisplay={!isStepwise}>
          <ChatInputBox showChatBalloon={showChatBalloon} title="電話番号を教えてください">
            <PhoneForm phoneNumberField={contractorFormPhoneNumberField} />
          </ChatInputBox>
        </FadeableBox>

        <FadeableBox fadeInCondition={formStepIndex >= 3} isAlwaysDisplay={!isStepwise}>
          <ChatInputBox showChatBalloon={showChatBalloon} title="お住まいについて教えてください">
            <AddressForms
              zipCodeField={contractorFormZipCodeField}
              prefectureField={contractorFormPrefectureField}
              cityField={contractorFormCityField}
              addressField={contractorFormAddressField}
              buildingNameAndRoomNumberField={contractorFormBuildingNameAndRoomNumberField}
              prefectureMode="code"
            />
          </ChatInputBox>
        </FadeableBox>
      </Flex>

      {isStepwise && formStepIndex <= 3 && (
        <Button
          type="button"
          size="md"
          m="auto"
          onClick={() => {
            if (formStepIndex === 3 && onCompleteAllSteps) {
              onCompleteAllSteps();
            }

            setFormStepIndex((current: number) => current + 1);
          }}
          isDisabled={!isValid}
        >
          次へ
        </Button>
      )}
    </Flex>
  );
};
